import { useMutation } from "@apollo/client";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreateTenantAnonymousDocument } from "../../gql/graphql";
import { useApp } from "../../hooks/app-context";
import { RouteConfig } from "../../routes/AuthRoutes";
import { AlertComponent } from "../shared/AlertComponent";
import { InputComponent } from "../shared/InputComponent";
import { LinkComponent } from "../shared/LinkComponent";
import { NblocksButton } from "../shared/NblocksButton";
import { TextComponent } from "../shared/TextComponent";
import { useTranslation } from "react-i18next";
import { DividerComponent } from "../shared/DividerComponent";
import { SsoButtonsComponent } from "./login/SsoButtonsComponent";
import { Transition } from "@headlessui/react";
const SignupComponent = ({
  didSignup,
  didClickFederatedSignup
}) => {
  const [createTenantAnonymous, {
    loading
  }] = useMutation(CreateTenantAnonymousDocument);
  const {
    logo,
    privacyPolicyUrl,
    termsOfServiceUrl
  } = useApp();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSsoAlternatives, setHasSsoAlternatives] = useState(false);
  const {
    t
  } = useTranslation();
  const [params] = useSearchParams();
  const [plan, currency, recurrenceInterval] = [params.get("plan"), params.get("currency"), params.get("recurrenceInterval")];
  const submit = async event => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMsg("");
    try {
      await createTenantAnonymous({
        variables: {
          tenant: {
            owner: {
              email,
              firstName,
              lastName
            },
            plan: plan ? plan : undefined,
            priceOffer: currency && recurrenceInterval ? {
              currency,
              recurrenceInterval
            } : undefined,
            logo,
            name: ""
          }
        }
      });
      didSignup(email);
      setIsLoading(false);
    } catch (error) {
      setErrorMsg(t("There was an error when creating the account. Try again, otherwise contact support."));
      setIsLoading(false);
    }
  };
  const ssoSignupMiddleware = type => {
    setErrorMsg("");
    didClickFederatedSignup(type);
  };
  const renderSignupAlternatives = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, hasSsoAlternatives && /*#__PURE__*/React.createElement("div", {
      className: "py-2"
    }, /*#__PURE__*/React.createElement(DividerComponent, {
      text: t("Or")
    })), /*#__PURE__*/React.createElement("div", {
      className: "space-y-2"
    }, /*#__PURE__*/React.createElement(SsoButtonsComponent, {
      label: "signup",
      didClickSsoBtn: ssoSignupMiddleware,
      hasAlternatives: setHasSsoAlternatives
    })));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "max-w-sm w-full mb-6"
  }, /*#__PURE__*/React.createElement(Transition, {
    show: !!errorMsg,
    enter: "transition duration-100 ease-out",
    enterFrom: "transform scale-95 opacity-0",
    enterTo: "transform scale-100 opacity-100",
    leave: "transition duration-75 ease-out",
    leaveFrom: "transform scale-100 opacity-100",
    leaveTo: "transform scale-95 opacity-0"
  }, /*#__PURE__*/React.createElement(AlertComponent, {
    type: "danger",
    title: t("An error occured"),
    messages: [errorMsg]
  }))), /*#__PURE__*/React.createElement("form", {
    onSubmit: event => submit(event),
    className: "space-y-6 max-w-sm w-full"
  }, /*#__PURE__*/React.createElement(InputComponent, {
    type: "email",
    label: t("Email address*"),
    placeholder: t("Enter your email"),
    name: "username",
    onChange: event => setEmail(event.target.value),
    value: email
  }), /*#__PURE__*/React.createElement(InputComponent, {
    type: "text",
    label: t("First name"),
    placeholder: t("Enter your first name"),
    name: "firstName",
    onChange: event => setFirstName(event.target.value),
    value: firstName
  }), /*#__PURE__*/React.createElement(InputComponent, {
    type: "text",
    label: t("Last name"),
    placeholder: t("Enter your last name"),
    name: "lastName",
    onChange: event => setLastName(event.target.value),
    value: lastName
  }), /*#__PURE__*/React.createElement("div", {
    className: "mt-8"
  }, /*#__PURE__*/React.createElement(TextComponent, {
    size: "sm"
  }, t("By proceeding you agree to the Nblocks"), "\xA0", /*#__PURE__*/React.createElement(LinkComponent, {
    type: "primary",
    to: privacyPolicyUrl,
    nativeBehavior: true,
    target: "_blank",
    size: "sm",
    className: "font-semibold"
  }, t("privacy policy")), "\xA0&\xA0", /*#__PURE__*/React.createElement(LinkComponent, {
    type: "primary",
    to: termsOfServiceUrl,
    nativeBehavior: true,
    target: "_blank",
    size: "sm",
    className: "font-semibold"
  }, t("terms of use")))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(NblocksButton, {
    submit: true,
    disabled: !email,
    size: "md",
    isLoading: isLoading,
    type: "primary",
    fullWidth: true
  }, t("Sign up"))), renderSignupAlternatives()), /*#__PURE__*/React.createElement("div", {
    className: "mt-8"
  }, /*#__PURE__*/React.createElement(TextComponent, {
    size: "sm"
  }, t("Already have an account?"), "\xA0", /*#__PURE__*/React.createElement(LinkComponent, {
    to: RouteConfig.login.loginScreen,
    type: "primary",
    size: "sm",
    className: "font-semibold"
  }, t("Log in")))));
};
export { SignupComponent };